/**
 * Work Orders Business Logic Hooks
 *
 * TanStack Query hooks for the Work Orders module. This module uses no Redux —
 * every read and write goes through these hooks (see the spec, §10 Phase 1).
 */

import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { appointmentKeys } from '../appointment';
import {
    cancelWorkOrderApi,
    changeWorkOrderStatusApi,
    convertWorkOrderToInvoiceApi,
    createWorkOrderApi,
    deleteWorkOrderApi,
    generateWorkOrderNumberApi,
    markWorkOrderAuthorizedApi,
    registerWorkOrderActionApi,
    sendWorkOrderEmailApi,
    updateWorkOrderApi,
    workOrderDetailApi,
    workOrderHistoryApi,
    workOrderListApi,
} from './workOrders.api';

// Query keys for the work orders domain
export const businessWorkOrdersKeys = {
    all: ['workOrders'],
    generateWorkOrderNumber: () => [...businessWorkOrdersKeys.all, 'generateWorkOrderNumber'],
    workOrdersList: (params) => [...businessWorkOrdersKeys.all, 'list', params],
    workOrdersInfiniteList: (params) => [...businessWorkOrdersKeys.all, 'infiniteList', params],
    detail: (id) => [...businessWorkOrdersKeys.all, 'detail', id],
    history: (id, params) => [...businessWorkOrdersKeys.all, 'history', id, params],
};

export const useGenerateWorkOrderNumber = (id, options = {}) => {
    return useQuery({
        queryKey: businessWorkOrdersKeys.generateWorkOrderNumber(),
        queryFn: () => generateWorkOrderNumberApi(),
        enabled: !id,
        staleTime: 0,
        gcTime: 0,
        refetchOnMount: true,
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        ...options,
    });
};

export const useWorkOrderList = (params, options = {}) => {
    return useQuery({
        queryKey: businessWorkOrdersKeys.workOrdersList(params),
        queryFn: () => workOrderListApi(params),
        // Keep the previous page on screen while the next one loads, so changing a filter,
        // sort or page does not flash the empty state.
        placeholderData: keepPreviousData,
        staleTime: 2 * 60 * 1000, // 2 minutes
        gcTime: 5 * 60 * 1000, // 5 minutes
        ...options,
    });
};

export const useWorkOrderDetail = (id, options = {}) => {
    return useQuery({
        queryKey: businessWorkOrdersKeys.detail(id),
        queryFn: () => workOrderDetailApi(id),
        enabled: !!id,
        staleTime: 0,
        gcTime: 0,
        ...options,
    });
};

/** Authorization history — GET history/:id, sorted by the API (spec §14.9). */
export const useWorkOrderHistory = (id, params = {}, options = {}) => {
    return useQuery({
        queryKey: businessWorkOrdersKeys.history(id, params),
        queryFn: () => workOrderHistoryApi(id, params),
        enabled: !!id,
        staleTime: 0,
        ...options,
    });
};

/**
 * Infinite work order list with search and pagination.
 * Same shape as useInfiniteInvoiceList so the two listings stay interchangeable.
 */
export const useInfiniteWorkOrderList = (params = {}, options = {}) => {
    const queryParams = { limit: 20, ...params };

    return useInfiniteQuery({
        queryKey: businessWorkOrdersKeys.workOrdersInfiniteList(queryParams),
        queryFn: ({ pageParam = 1 }) => workOrderListApi({ ...queryParams, page: pageParam }),
        getNextPageParam: (lastPage, allPages) => {
            let hasMore = false;

            if (Array.isArray(lastPage)) {
                hasMore = lastPage.length === queryParams.limit;
            } else if (lastPage && typeof lastPage === 'object') {
                if (lastPage.totalPages !== undefined) {
                    hasMore = allPages.length < lastPage.totalPages;
                } else if (Array.isArray(lastPage.data)) {
                    hasMore = lastPage.data.length === queryParams.limit;
                }
            }

            return hasMore ? allPages.length + 1 : undefined;
        },
        staleTime: 2 * 60 * 1000,
        gcTime: 5 * 60 * 1000,
        refetchOnWindowFocus: false,
        ...options,
    });
};

/** Invalidate every work order query — call after any mutation. */
export const useRefetchWorkOrders = () => {
    const queryClient = useQueryClient();
    return () => queryClient.invalidateQueries({ queryKey: businessWorkOrdersKeys.all, refetchType: 'all' });
};

export const useCreateWorkOrder = (options = {}) => {
    const refetch = useRefetchWorkOrders();
    return useMutation({
        mutationFn: (variables) => {
            if (variables && typeof variables === 'object' && ('payload' in variables || 'params' in variables)) {
                return createWorkOrderApi(variables.payload, variables.params);
            }
            return createWorkOrderApi(variables);
        },
        onSuccess: (...args) => {
            refetch();
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

export const useUpdateWorkOrder = (options = {}) => {
    const refetch = useRefetchWorkOrders();
    return useMutation({
        mutationFn: ({ id, payload, params }) => updateWorkOrderApi(id, payload, params),
        onSuccess: (...args) => {
            refetch();
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

export const useDeleteWorkOrder = (options = {}) => {
    const refetch = useRefetchWorkOrders();
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => deleteWorkOrderApi(id),
        onSuccess: (...args) => {
            refetch();
            /*
             * An appointment shows REQUEST AUTHORIZATION or VIEW AUTHORIZATION from the work order on its
             * own record. Deleting the work order leaves that cached appointment pointing at a record that
             * no longer exists, so the appointment queries are refreshed too and the button falls back to
             * REQUEST AUTHORIZATION.
             */
            queryClient.invalidateQueries({ queryKey: appointmentKeys.all, refetchType: 'all' });
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

/**
 * Mark a work order authorized.
 * NOTE: this is the inventory consumption event (spec §9) — out-of-stock parts must be
 * resolved before this is allowed to run.
 */
export const useMarkWorkOrderAuthorized = (options = {}) => {
    const refetch = useRefetchWorkOrders();
    return useMutation({
        mutationFn: (id) => markWorkOrderAuthorizedApi(id),
        onSuccess: (...args) => {
            refetch();
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

/** Generic status transition — PUT change-status/:id { status }. */
export const useChangeWorkOrderStatus = (options = {}) => {
    const refetch = useRefetchWorkOrders();
    return useMutation({
        mutationFn: ({ id, status }) => changeWorkOrderStatusApi(id, status),
        onSuccess: (...args) => {
            refetch();
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

export const useCancelWorkOrder = (options = {}) => {
    const refetch = useRefetchWorkOrders();
    return useMutation({
        mutationFn: ({ id }) => cancelWorkOrderApi(id),
        onSuccess: (...args) => {
            refetch();
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

/**
 * Register an action (download, share, …) against a work order so it lands in the history.
 * Only the history queries are invalidated — the work order itself is unchanged.
 */
export const useRegisterWorkOrderAction = (options = {}) => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ type, id }) => registerWorkOrderActionApi(type, id),
        onSuccess: (...args) => {
            queryClient.invalidateQueries({ queryKey: [...businessWorkOrdersKeys.all, 'history'], refetchType: 'all' });
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

export const useConvertWorkOrderToInvoice = (options = {}) => {
    const refetch = useRefetchWorkOrders();
    return useMutation({
        mutationFn: (id) => convertWorkOrderToInvoiceApi(id),
        onSuccess: (...args) => {
            refetch();
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};

/** Send Work Order PDF via email to customer. Invalidates work orders to refresh list and detail states. */
export const useSendWorkOrderEmail = (options = {}) => {
    const refetch = useRefetchWorkOrders();
    return useMutation({
        mutationFn: (id) => sendWorkOrderEmailApi(id),
        onSuccess: (...args) => {
            refetch();
            options?.onSuccess?.(...args);
        },
        ...options,
    });
};
